import { DateTime } from '@ksyos/date-time';
import { Koi } from '@ksyos/koi';
import type { TypedSchema } from '@common/validation/utils/typedSchemaType';
import {
    bariPostNurseSpecialistFollowUpExperts,
    FollowUpStatusOpties,
    ontlastingType,
    orderedPractitionerTypes,
    TypeBehandelaar,
} from '@common/customControls/bariatrics';
import { getProcessActivityAssetsSchema } from '@common/validation/processActivityAsset';
import {
    alcoholEenhedenPerWeekSchema,
    hba1cSchema,
    ontlastingFrequentiePerDagSchema,
} from '@common/validation/schemas/numberSchemas';
import { getTextAreaConditionalSchema } from '@common/validation/utils/getTextAreaConditionalSchema';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';
import type {
    BariPostNurseSpecialistFollowUpBeoordelingEnVervolgDataBase,
    BariPostNurseSpecialistFollowUpComorbiditeitenDataBase,
    BariPostNurseSpecialistFollowUpLeefstijlEnMedicatieDataBase,
} from './bariPostNurseSpecialistFollowUpDataBase';

export const bariPostNurseSpecialistFollowUpLeefstijlEnMedicatieBaseFinal: TypedSchema<BariPostNurseSpecialistFollowUpLeefstijlEnMedicatieDataBase> =
    {
        alcohol: Koi.boolean().allow(null),
        alcohol_aantal_eenheden_per_week: Koi.when('alcohol', {
            is: true,
            otherwise: Koi.valid(null),
            then: alcoholEenhedenPerWeekSchema.allow(null),
        }),
        alcohol_toelichting: getTextAreaConditionalSchema('alcohol', true),
        beloop_aanvullende_anamnese: getTextAreaStringSchema(true),
        huidige_medicatie: getTextAreaConditionalSchema('medicatie', true),
        medicatie: Koi.boolean().allow(null),
        multivitaminen: Koi.boolean().allow(null),
        multivitaminen_toelichting: getTextAreaConditionalSchema('multivitaminen'),
        ontlasting: Koi.string()
            .valid(...ontlastingType)
            .allow(null),
        ontlasting_frequentie_per_dag: ontlastingFrequentiePerDagSchema.allow(null),
    };

// NB since all _toelichtingen fields are in the base data, they have to be in the schema here,
// but actually these _toelichtingen validations only apply to the V1 form.
export const bariPostNurseSpecialistFollowUpComorbiditeitenBaseFinal: TypedSchema<BariPostNurseSpecialistFollowUpComorbiditeitenDataBase> =
    {
        diabetes_mellitus: Koi.koi().enum(FollowUpStatusOpties),
        diabetes_mellitus_medicamenteuze_veranderingen_toelichting: getTextAreaConditionalSchema(
            'diabetes_mellitus_medicamenteuze_veranderingen'
        ),
        dumpingklachten: Koi.boolean(),
        dumpingklachten_options: Koi.when('dumpingklachten', {
            is: true,
            otherwise: Koi.object({
                dumpingklachten_late_dumping: Koi.valid(null),
                dumpingklachten_vroege_dumping: Koi.valid(null),
            }),
            then: Koi.object({
                dumpingklachten_late_dumping: Koi.boolean().allow(null),
                dumpingklachten_vroege_dumping: Koi.boolean().allow(null),
            }).anyFilledIn(),
        }),
        dumpingklachten_toelichting: getTextAreaConditionalSchema('dumpingklachten'),
        dyslipidemie: Koi.koi().enum(FollowUpStatusOpties),
        dyslipidemie_medicamenteuze_veranderingen_toelichting: getTextAreaConditionalSchema(
            'dyslipidemie_medicamenteuze_veranderingen'
        ),
        gastroesophageal_reflux_disease: Koi.koi().enum(FollowUpStatusOpties),

        gastroesophageal_reflux_disease_medicamenteuze_veranderingen_toelichting: getTextAreaConditionalSchema(
            'gastroesophageal_reflux_disease_medicamenteuze_veranderingen'
        ),
        gewrichtsklachten: Koi.koi().enum(FollowUpStatusOpties),
        gewrichtsklachten_therapeutische_veranderingen_toelichting: getTextAreaConditionalSchema(
            'gewrichtsklachten_therapeutische_veranderingen'
        ),
        hba1c: hba1cSchema,
        hypertensie: Koi.koi().enum(FollowUpStatusOpties),
        hypertensie_medicamenteuze_veranderingen_toelichting: getTextAreaConditionalSchema(
            'hypertensie_medicamenteuze_veranderingen'
        ),
        osas: Koi.koi().enum(FollowUpStatusOpties),
        osas_therapeutische_veranderingen_toelichting: getTextAreaConditionalSchema(
            'osas_therapeutische_veranderingen'
        ),
        reflux_klachten: Koi.boolean(),
        reflux_klachten_toelichting: getTextAreaConditionalSchema('reflux_klachten'),
    };

export const bariPostNurseSpecialistFollowUpBeoordelingEnVervolgBaseFinal: TypedSchema<BariPostNurseSpecialistFollowUpBeoordelingEnVervolgDataBase> =
    {
        assets: Koi.when('extern_doorverwijzen', {
            is: true,
            otherwise: getProcessActivityAssetsSchema(),
            then: getProcessActivityAssetsSchema({ min: 1 }),
        }),
        beleid: getTextAreaStringSchema(true),
        datum_verwijzing: Koi.when('extern_doorverwijzen', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.koi().dateSameOrBefore(DateTime.today().format('YYYY-MM-DD')),
        }),
        expert: Koi.when('interne_expert_inschakelen', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.valid(...bariPostNurseSpecialistFollowUpExperts),
        }),
        expert_hulpvraag: Koi.when('interne_expert_inschakelen', {
            is: true,
            otherwise: Koi.valid(null),
            then: getTextAreaStringSchema(),
        }),
        extern_doorverwijzen: Koi.boolean(),
        interne_expert_inschakelen: Koi.boolean(),
        naam_externe_behandelaar: Koi.when('extern_doorverwijzen', {
            is: true,
            otherwise: Koi.valid(null),
            then: getTextAreaStringSchema(),
        }),
        reden_doorverwijzing: Koi.when('extern_doorverwijzen', {
            is: true,
            otherwise: Koi.valid(null),
            then: getTextAreaStringSchema(),
        }),
        type_behandelaar: Koi.when('extern_doorverwijzen', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.valid(...orderedPractitionerTypes),
        }),
        type_behandelaar_overig: Koi.when('type_behandelaar', {
            is: TypeBehandelaar.overig,
            otherwise: Koi.valid(null),
            then: getTextAreaStringSchema(),
        }),
    };
